import {Component, ElementRef, inject, Input, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Project, ServiceControllerService, SimplePlace} from "../../../api";
import {AbstractControl, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {KeyValuePipe} from "@angular/common";
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faMagnifyingGlass} from '@fortawesome/pro-solid-svg-icons';
import {ProjectStatePipe, ProjectStateReversePipe} from "../../../pipes/project-state.pipe";
import {
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
  BicInputComponent,
  BicSelectComponent
} from "@buildinginformationcloud/bic-ui-components";
import {CountryPipe} from '../../../pipes/country.pipe';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {Subject} from 'rxjs';
import StateEnum = Project.StateEnum;
import CountryEnum = Project.CountryEnum;

/** Land, das bei leerem Adressfeld vorbelegt wird (ISO-3-Code). */
export const DEFAULT_COUNTRY: CountryEnum = CountryEnum.DEU;

/**
 * Setzt Deutschland, wenn das Land leer ist; überschreibt nie einen gespeicherten Wert;
 * nur bei bearbeitbarem Control.
 * @returns true, wenn der Default gesetzt wurde
 */
export function applyDefaultCountry(control: AbstractControl | null | undefined): boolean {
  if (!control || !control.enabled) {
    return false;
  }
  const value = control.value;
  if (value !== null && value !== undefined && value !== '') {
    return false;
  }
  control.setValue(DEFAULT_COUNTRY);
  return true;
}

/** Länder, die in der Länderauswahl in dieser Reihenfolge vor allen anderen stehen (DACH). */
export const PRIORITY_COUNTRIES: readonly string[] = [CountryEnum.DEU, CountryEnum.AUT, CountryEnum.CHE];

/** Eine Option der Länderauswahl: ISO-3-Code und deutsches Label. */
export interface CountryOption {
  key: string;
  label: string;
}

/** Die Länderauswahl in zwei Gruppen: DACH oben, darunter der Rest. */
export interface CountryOptionGroups {
  priority: CountryOption[];
  rest: CountryOption[];
}

/**
 * Baut die Länderoptionen aus einer (ggf. gefilterten) Länderliste: DACH-Länder in der Reihenfolge
 * von PRIORITY_COUNTRIES, nur soweit sie in der Liste stehen; der Rest alphabetisch nach deutschem Label.
 */
export function buildCountryOptions(countries: Record<string, string>): CountryOptionGroups {
  const countryPipe = new CountryPipe();
  const options = Object.values(countries).map(key => ({key, label: countryPipe.transform(key as CountryEnum)}));
  return {
    priority: PRIORITY_COUNTRIES
      .map(key => options.find(option => option.key === key))
      .filter((option): option is CountryOption => !!option),
    rest: options
      .filter(option => !PRIORITY_COUNTRIES.includes(option.key))
      .sort((a, b) => a.label.localeCompare(b.label, 'de')),
  };
}

/**
 * Filtert die Länderoptionen wie die Dateisuche im MyCheck-Testlauf: jedes Wort muss im Label
 * (oder im ISO-Code) vorkommen, ohne Groß/Klein. Das gewählte Land bleibt immer an seinem Platz
 * in der Liste – `bic-select` liest seine Anzeige aus den vorhandenen Optionen und zeigte sonst
 * wieder den Platzhalter.
 * @returns die sichtbaren Gruppen und ob die Suche kein Land trifft
 */
export function filterCountryOptions(
  groups: CountryOptionGroups,
  term: string,
  keepKey: string | null | undefined,
): {visible: CountryOptionGroups; empty: boolean} {
  const words = term.normalize('NFC').toLocaleLowerCase('de').split(/\s+/).filter(Boolean);
  if (!words.length) return {visible: groups, empty: false};
  const matches = (option: CountryOption): boolean => {
    const label = option.label.normalize('NFC').toLocaleLowerCase('de');
    const key = option.key.toLocaleLowerCase('de');
    return words.every(word => label.includes(word) || key.includes(word));
  };
  const matchCount = [...groups.priority, ...groups.rest].filter(matches).length;
  const keep = (option: CountryOption): boolean => matches(option) || option.key === keepKey;
  return {
    visible: {priority: groups.priority.filter(keep), rest: groups.rest.filter(keep)},
    empty: matchCount === 0,
  };
}

@Component({
  selector: 'app-address-input',
  templateUrl: './address-input.component.html',
  styleUrls: ['./address-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, ReactiveFormsModule, KeyValuePipe, ProjectStatePipe, FaIconComponent, BicInputComponent, BicDropdownComponent, BicDropdownElementComponent, BicDropdownTriggerDirective, BicSelectComponent]
})
export class AddressInputComponent implements OnInit {
  serviceControllerService = inject(ServiceControllerService);


  @Input() formGroup!: FormGroup;
  @Input() formControlNameStreet = 'street';
  @Input() formControlNameHouseNumber = 'houseNumber';
  @Input() formControlNameCity = 'city';
  @Input() formControlNameZipCode = 'zipCode';
  @Input() formControlNameCountry = 'country';
  @Input() formControlNameState = 'state';

  @Input() zipCodeDisabled = false;
  @Input() cityDisabled = false;
  @Input() countryDisabled = false;
  @Input() countrySelectDisabled = false;
  @Input() stateDisabled = true;
  @Input() displayState = true;
  @Input() displayPlotNumber = false;
  @Input() formControlNamePlotNumber = 'plotNumber'

  locationGuess: SimplePlace[] = [];
  @ViewChild("dropdown") dropdown!: BicDropdownComponent;
  @ViewChild('street', {read: ElementRef}) streetInput!: ElementRef;

  states: typeof StateEnum = StateEnum;
  countries: typeof CountryEnum = CountryEnum;

  // filtered list used by the template
  filteredCountries: typeof CountryEnum = this.countries;

  protected readonly faMagnifyingGlass = faMagnifyingGlass;
  /** Alle Länderoptionen aus filteredCountries, DACH oben. */
  private countryOptions: CountryOptionGroups = {priority: [], rest: []};
  /** Nach der Suche sichtbare Länderoptionen (das gewählte Land bleibt immer drin). */
  visibleCountries: CountryOptionGroups = {priority: [], rest: []};
  countrySearch = '';
  /** Die Suche trifft kein Land. */
  countrySearchEmpty = false;
  /** Trennlinie zwischen DACH und dem Rest – nur, wenn beide Gruppen sichtbare Einträge haben. */
  showCountrySeparator = false;

  private addressChangedSubject = new Subject<string>();


  ngOnInit(): void {
    this.countryOptions = buildCountryOptions(this.filteredCountries);
    this.updateVisibleCountries();
    this.onCountryChange(this.formGroup.get('country')?.value);
    this.formGroup.get('country')?.valueChanges.subscribe(country => {
      this.onCountryChange(country);
    });
    this.formGroup.get(this.formControlNameCountry)?.valueChanges.subscribe(() => this.updateVisibleCountries());
    // Setup debounced address change listener
    this.addressChangedSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(value => {
      if (value && value.length > 2) {
        this.serviceControllerService.getLocationSuggestions(value).subscribe(locations => {
          this.locationGuess = locations;
        });
      }
    });
  }

  onCountryChange(country: string) {
    const stateControl = this.formGroup.get('state');
    this.formGroup.get(this.formControlNameZipCode)?.updateValueAndValidity({emitEvent: false});
    if (this.stateDisabled || this.isCountrySelectDisabled()) {
      stateControl?.disable();
      return;
    }
    if (country === 'DEU') {
      stateControl?.enable();
    } else {
      stateControl?.disable();
      stateControl?.reset();
    }
  }

  updateLocation(location: SimplePlace) {
    let projectStateReversePipe = new ProjectStateReversePipe()

    this.formGroup.get(this.formControlNameStreet)?.setValue(location.street);
    this.formGroup.get(this.formControlNameHouseNumber)?.setValue(location.addressNumber);
    this.formGroup.get(this.formControlNameCity)?.setValue(location.municipality);
    this.formGroup.get(this.formControlNameZipCode)?.setValue(location.postalCode);
    this.formGroup.get(this.formControlNameCountry)?.setValue(location.country);
    if (location.region != null && location.country == "DEU") {
      this.formGroup.get(this.formControlNameState)?.enable();
      this.formGroup.get(this.formControlNameState)?.setValue(projectStateReversePipe.transform(location.region));
    } else {
      this.formGroup.get(this.formControlNameState)?.setValue(undefined);
      this.formGroup.get(this.formControlNameState)?.disable();
    }

    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();

    this.dropdown.hide()
  }

  onCountrySearch(term: string): void {
    this.countrySearch = term;
    this.updateVisibleCountries();
  }

  private updateVisibleCountries(): void {
    const selected = this.formGroup.get(this.formControlNameCountry)?.value;
    const {visible, empty} = filterCountryOptions(this.countryOptions, this.countrySearch, selected);
    this.visibleCountries = visible;
    this.countrySearchEmpty = empty;
    this.showCountrySeparator = visible.priority.length > 0 && visible.rest.length > 0;
  }

  addressChanged(value: string) {
    if (this.streetInput) {
      this.dropdown.width = this.streetInput.nativeElement.offsetWidth + 'px';
    }
    if (value && value.length > 2) {
      this.dropdown.show();
      this.addressChangedSubject.next(value);
    }
  }

  isCountrySelectDisabled(): boolean {
    return this.countrySelectDisabled || this.formGroup.disabled;
  }

  getCountryLabel(): string {
    const country = this.formGroup.get(this.formControlNameCountry)?.value;
    return country ? new CountryPipe().transform(country) ?? '' : '';
  }

  hasError(controlName: string, error: string): boolean {
    const control = this.formGroup.get(controlName);
    return !!control && control.touched && control.hasError(error);
  }

  shouldDisplayState(): boolean {
    return !this.displayState && this.formGroup.get(this.formControlNameCountry)?.value === 'DEU';
  }


}
